/**
 * Combined Activity Highlights across groups the user belongs to.
 * Respects the page-level group filters via the `groups` prop.
 */

import { useCallback, useMemo } from 'react';
import {
  ArrowsClockwiseIcon,
  CalendarCheckIcon,
  CalendarDotsIcon,
  CalendarPlusIcon,
  CertificateIcon,
  ChartBarIcon,
  ChatTextIcon,
  CheckCircleIcon,
  ClipboardTextIcon,
  FileIcon,
  FileArrowUpIcon,
  FilePlusIcon,
  FileTextIcon,
  GraduationCapIcon,
  PlusIcon,
  UserMinusIcon,
  UserPlusIcon,
  UsersThreeIcon
} from '@phosphor-icons/react';
import useTenantNavigate from 'src/hooks/useTenantNavigate';
import { useOpenAction } from './ActionViewer';
import Loading from '@/components/ui/Loading';
import Panel from '@/components/ui/Panel';
import type { DirectoryGroupCard, RecentActivityItem } from '../api';
import { hasOwnerPermission } from '../permissions';
import type { Group } from '../types';
import { groupEvidenceTabPath } from './GroupEvidenceTab';
import { groupDashboardsTabPath } from './GroupDashboardsTab';
import {
  ActivityTimeline,
  groupActivityByDay,
  HOME_TIMELINE_KINDS,
  joinRequestsToFeedItems,
  mergeHomeTimelineItems,
  RequiresActionCardList,
  type HomeFeedItem,
  unfinalisedMeetingToFeedItem
} from './GroupHomeTab';
import { statutoryDocumentExpiryFeedItems } from './StatutoryComplianceSummary';

const PREVIEW_COUNT = 12;

function activityTimeMs(value: string | null): number {
  if (!value) return 0;
  const date = new Date(value.includes('Z') ? value : `${value}Z`);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

export function getRecentActivityIcon(item: RecentActivityItem) {
  switch (item.kind) {
    case 'action_created':
      return <PlusIcon size={18} weight="light" />;
    case 'action_state_changed':
      return <ArrowsClockwiseIcon size={18} weight="light" />;
    case 'action_progress_update':
      return <ChatTextIcon size={18} weight="light" />;
    case 'extraction_meeting':
      return <ClipboardTextIcon size={18} weight="light" />;
    case 'extraction_report':
      return <FileArrowUpIcon size={18} weight="light" />;
    case 'resource_shared':
      return <FileTextIcon size={18} weight="light" />;
    case 'meeting_approval':
      return <CheckCircleIcon size={18} weight="light" />;
    case 'member_added':
      return <UserPlusIcon size={18} weight="light" />;
    case 'group_created':
      return <UsersThreeIcon size={18} weight="light" />;
    case 'meeting_added':
      return <CalendarPlusIcon size={18} weight="light" />;
    case 'meeting_series_added':
      return <CalendarDotsIcon size={18} weight="light" />;
    case 'meeting_resource_added':
      return <FilePlusIcon size={18} weight="light" />;
    case 'meeting_finalised':
      return <CalendarCheckIcon size={18} weight="light" />;
    case 'appointment_created':
      return <GraduationCapIcon size={18} weight="light" />;
    case 'appointment_revoked':
      return <UserMinusIcon size={18} weight="light" />;
    case 'certificate_added':
      return <CertificateIcon size={18} weight="light" />;
    case 'dashboard_data_entered':
      return <ChartBarIcon size={18} weight="light" />;
    default:
      return <FileIcon size={18} weight="light" />;
  }
}

export function useGroupsHomeActivity(
  groups: Group[],
  snapshotByGroupId: Map<string, DirectoryGroupCard>,
  snapshotLoading: boolean
) {
  const navigate = useTenantNavigate();
  const openAction = useOpenAction();

  const isLoadingActivity = snapshotLoading;
  const isLoadingRequiresAction = snapshotLoading;

  const homeActivity = useMemo(() => {
    const combined: HomeFeedItem[] = [];
    groups.forEach((group) => {
      const items = snapshotByGroupId.get(group.id)?.recent_activity ?? [];
      const merged = mergeHomeTimelineItems(items, [], HOME_TIMELINE_KINDS).filter(
        (item) => item.kind !== 'action_created'
      );
      for (const item of merged) {
        combined.push({
          ...item,
          groupId: group.id,
          groupName: group.name,
          subtitleParts: [group.name, ...item.subtitleParts]
        });
      }
    });
    combined.sort((a, b) => activityTimeMs(b.timestamp) - activityTimeMs(a.timestamp));
    return combined;
  }, [groups, snapshotByGroupId]);

  const requiresActionItems = useMemo(() => {
    const combined: HomeFeedItem[] = [];
    groups.forEach((group) => {
      for (const meeting of snapshotByGroupId.get(group.id)?.unfinalised_meetings ?? []) {
        combined.push({
          ...unfinalisedMeetingToFeedItem(meeting),
          groupId: group.id,
          groupName: group.name
        });
      }
      const pending = (snapshotByGroupId.get(group.id)?.pending_join_requests ?? []).filter(
        (request) => request.status === 'pending'
      );
      for (const item of joinRequestsToFeedItems(pending)) {
        combined.push({
          ...item,
          groupId: group.id,
          groupName: group.name
        });
      }
      for (const alert of snapshotByGroupId.get(group.id)?.training_alerts ?? []) {
        combined.push({
          kind: alert.kind,
          title: alert.title,
          subtitleParts: alert.subtitle_parts,
          timestamp: null,
          relevant_to_user: true,
          target_ids: [alert.target_id],
          groupId: group.id,
          groupName: group.name
        });
      }
      if (hasOwnerPermission(group)) {
        const statutoryDocuments = snapshotByGroupId.get(group.id)?.statutory_documents ?? [];
        for (const item of statutoryDocumentExpiryFeedItems(statutoryDocuments)) {
          combined.push({
            ...item,
            groupId: group.id,
            groupName: group.name
          });
        }
      }
    });
    return combined;
  }, [groups, snapshotByGroupId]);

  const previewGroups = useMemo(() => groupActivityByDay(homeActivity.slice(0, PREVIEW_COUNT)), [homeActivity]);

  const handleItemClick = useCallback(
    (item: HomeFeedItem) => {
      if (!item.groupId) return;
      if (item.kind === 'join_requested') {
        navigate(`/groups/${item.groupId}/people`);
        return;
      }
      if (item.kind === 'appointment_expiring') {
        navigate(`/groups/${item.groupId}/training`);
        return;
      }
      if (item.kind === 'statutory_document_expired' || item.kind === 'statutory_document_expiring') {
        navigate(groupEvidenceTabPath(item.groupId));
        return;
      }
      switch (item.kind) {
        case 'action_created':
        case 'action_state_changed':
        case 'action_progress_update':
          if (item.target_ids.length === 1 && openAction) {
            openAction(item.target_ids[0]);
            return;
          }
          navigate(`/groups/${item.groupId}/actions`);
          return;
        case 'extraction_meeting':
        case 'extraction_report':
          navigate(`/groups/${item.groupId}/actions`);
          return;
        case 'meeting_added':
        case 'meeting_resource_added':
        case 'meeting_finalised':
        case 'meeting_approval':
        case 'meeting_needs_finalising':
          if (item.target_ids[0]) {
            navigate(`/meetings/${item.target_ids[0]}`);
            return;
          }
          navigate(`/groups/${item.groupId}/meetings`);
          return;
        case 'meeting_series_added':
          navigate(`/groups/${item.groupId}/meetings`);
          return;
        case 'member_added':
          navigate(`/groups/${item.groupId}/people`);
          return;
        case 'appointment_created':
        case 'appointment_revoked':
          navigate(`/groups/${item.groupId}/training`);
          return;
        case 'dashboard_data_entered':
          navigate(groupDashboardsTabPath(item.groupId, item.target_ids.length === 1 ? item.target_ids[0] : null));
          return;
        case 'resource_shared':
          navigate(`/groups/${item.groupId}/files`);
          return;
        default:
          navigate(`/groups/${item.groupId}`);
      }
    },
    [navigate, openAction]
  );

  return {
    homeActivity,
    requiresActionItems,
    previewGroups,
    isLoading: isLoadingActivity || isLoadingRequiresAction,
    handleItemClick
  };
}

function itemCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'}`;
}

function HighlightsColumnHeader({ title, count }: { title: string; count: number }) {
  return (
    <div className="flex items-center gap-2 min-w-0">
      <h3 className="text-lg font-semibold text-content-default">{title}</h3>
      <span className="text-xs text-content-subtle bg-gray-100 rounded-full px-2 py-0.5">{itemCountLabel(count)}</span>
    </div>
  );
}

function HighlightsEmpty({ message }: { message: string }) {
  return (
    <div className="text-center py-10 text-content-subtle border border-dashed border-border-subtle rounded-xl">
      <FileIcon size={40} weight="light" className="mx-auto mb-3 opacity-40" />
      <p className="text-sm">{message}</p>
    </div>
  );
}

interface GroupsActivityHighlightsProps {
  groups: Group[];
  homeActivity: HomeFeedItem[];
  requiresActionItems: HomeFeedItem[];
  previewGroups: ReturnType<typeof groupActivityByDay>;
  isLoading: boolean;
  onItemClick: (item: HomeFeedItem) => void;
}

export default function GroupsActivityHighlights({
  groups,
  homeActivity,
  requiresActionItems,
  previewGroups,
  isLoading,
  onItemClick
}: GroupsActivityHighlightsProps) {
  const noMatchingGroups = !isLoading && groups.length === 0;
  const timelineEmpty = !isLoading && groups.length > 0 && homeActivity.length === 0;
  const requiresEmpty = !isLoading && requiresActionItems.length === 0 && groups.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
      <Panel className="group-hub-recent-activity min-w-0">
        <HighlightsColumnHeader title="Activity Timeline" count={homeActivity.length} />
        {isLoading && homeActivity.length === 0 && <Loading text="Loading activity..." />}
        {noMatchingGroups && <HighlightsEmpty message="No groups match the current filters." />}
        {timelineEmpty && <HighlightsEmpty message="No activity highlights in your groups yet." />}
        {previewGroups.length > 0 && (
          <ActivityTimeline groups={previewGroups} onItemClick={onItemClick} getActivityIcon={getRecentActivityIcon} />
        )}
      </Panel>
      <Panel className="min-w-0">
        <HighlightsColumnHeader title="Requires Action" count={requiresActionItems.length} />
        {isLoading && requiresActionItems.length === 0 && <Loading text="Loading activity..." />}
        {noMatchingGroups && <HighlightsEmpty message="No groups match the current filters." />}
        {requiresEmpty && <HighlightsEmpty message="Nothing needs your action right now." />}
        {requiresActionItems.length > 0 && (
          <RequiresActionCardList
            items={requiresActionItems}
            getActivityIcon={getRecentActivityIcon}
            onItemClick={onItemClick}
            showGroupName
          />
        )}
      </Panel>
    </div>
  );
}
